export type GraphJsonEditorPayload = {
  adjacencyList: Record<string, string[]>
  meta: Record<
    string,
    {
      niche: number
      learning_order: number
    }
  >
}

export type GraphJsonReasonRole = 'origin' | 'propagated'

export interface GraphJsonReasonMetadata {
  role: GraphJsonReasonRole
  originClientId?: string
  originCoachUserId?: string | null
  originCoachName?: string
  originAdjustmentId?: string
}

export interface GraphJsonSnapshotDiffRow {
  label: string
  previousValue: string
  currentValue: string
}

const REASON_PREFIX = 'graph-tab-json-editor'

export function buildGraphJsonReason(
  metadata: GraphJsonReasonMetadata,
): string {
  const parts = [`${REASON_PREFIX}|role=${metadata.role}`]
  if (metadata.role === 'propagated') {
    if (metadata.originClientId) {
      parts.push(`originClientId=${metadata.originClientId}`)
    }
    if (metadata.originCoachUserId) {
      parts.push(`originCoachUserId=${metadata.originCoachUserId}`)
    }
    if (metadata.originCoachName) {
      parts.push(
        `originCoachName=${encodeURIComponent(metadata.originCoachName)}`,
      )
    }
    if (metadata.originAdjustmentId) {
      parts.push(`originAdjustmentId=${metadata.originAdjustmentId}`)
    }
  }
  return parts.join('|')
}

export function parseGraphJsonReason(
  reason: string | null | undefined,
): GraphJsonReasonMetadata | null {
  if (!reason?.startsWith(REASON_PREFIX)) return null
  const segments = reason.split('|').slice(1)
  const metadata: GraphJsonReasonMetadata = { role: 'origin' }
  for (const segment of segments) {
    const [key, ...rest] = segment.split('=')
    const value = rest.join('=')
    if (key === 'role' && (value === 'origin' || value === 'propagated')) {
      metadata.role = value
    } else if (key === 'originClientId') {
      metadata.originClientId = value
    } else if (key === 'originCoachUserId') {
      metadata.originCoachUserId = value
    } else if (key === 'originCoachName') {
      metadata.originCoachName = decodeURIComponent(value)
    } else if (key === 'originAdjustmentId') {
      metadata.originAdjustmentId = value
    }
  }
  return metadata
}

function formatMetaValue(
  meta: GraphJsonEditorPayload['meta'][string] | undefined,
): string {
  if (!meta) return 'unset'
  return `niche=${meta.niche}, order=${meta.learning_order}`
}

function edgeSet(payload: GraphJsonEditorPayload): Set<string> {
  const edges = new Set<string>()
  for (const [prerequisiteKey, targets] of Object.entries(
    payload.adjacencyList,
  )) {
    for (const targetKey of targets) {
      edges.add(`${prerequisiteKey}->${targetKey}`)
    }
  }
  return edges
}

export function buildGraphJsonSnapshotDiff(
  before: GraphJsonEditorPayload | null | undefined,
  after: GraphJsonEditorPayload | null | undefined,
): GraphJsonSnapshotDiffRow[] {
  if (!before && !after) return []
  const previous = before ?? {
    adjacencyList: {},
    meta: {},
  }
  const next = after ?? {
    adjacencyList: {},
    meta: {},
  }

  const rows: GraphJsonSnapshotDiffRow[] = []
  const metaKeys = new Set([
    ...Object.keys(previous.meta),
    ...Object.keys(next.meta),
  ])
  for (const key of [...metaKeys].sort((a, b) => a.localeCompare(b))) {
    const prevMeta = previous.meta[key]
    const nextMeta = next.meta[key]
    const prevJson = JSON.stringify(prevMeta ?? null)
    const nextJson = JSON.stringify(nextMeta ?? null)
    if (prevJson === nextJson) continue
    rows.push({
      label: `meta:${key}`,
      previousValue: formatMetaValue(prevMeta),
      currentValue: formatMetaValue(nextMeta),
    })
  }

  const beforeEdges = edgeSet(previous)
  const afterEdges = edgeSet(next)
  for (const edge of [...afterEdges].sort()) {
    if (!beforeEdges.has(edge)) {
      rows.push({
        label: 'adjacency added',
        previousValue: 'absent',
        currentValue: edge,
      })
    }
  }
  for (const edge of [...beforeEdges].sort()) {
    if (!afterEdges.has(edge)) {
      rows.push({
        label: 'adjacency removed',
        previousValue: edge,
        currentValue: 'absent',
      })
    }
  }

  if (rows.length === 0) {
    rows.push({
      label: 'graph JSON',
      previousValue: 'unchanged',
      currentValue: 'unchanged',
    })
  }

  return rows
}

export function graphJsonAttributionSummary(
  metadata: GraphJsonReasonMetadata | null,
  coachName: string,
  coachUserId: string | null | undefined,
): string {
  if (!metadata || metadata.role === 'origin') {
    return `Graph JSON editor state saved by ${coachName}${coachUserId ? ` (${coachUserId})` : ''}.`
  }
  const originCoach = metadata.originCoachName ?? 'Unknown coach'
  const originUserId = metadata.originCoachUserId ?? 'unknown'
  const originClient = metadata.originClientId ?? 'unknown client'
  return `Global graph JSON updated by ${originCoach} (${originUserId}) while viewing client ${originClient}.`
}
